import { useCallback, useEffect, useRef } from 'react';

export interface WsMessage {
  type: string;
  payload?: unknown;
}

interface UseWebSocketOptions {
  onMessage?: (message: WsMessage) => void;
  enabled?: boolean;
}

const RECONNECT_DELAY_MS = 3_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 30_000;

// AC8's backoff ladder needs six consecutive non-opening attempts to reach the 30s cap,
// so this must sit above that. It bounds a rejected upgrade at roughly three minutes.
const MAX_ATTEMPTS_WITHOUT_OPEN = 8;

const POLICY_VIOLATION_CODE = 1008;
const NO_RETRY_CODE_MIN = 4000;
const NO_RETRY_CODE_MAX = 4009;

const isFinalClose = (code: number) =>
  code === POLICY_VIOLATION_CODE || (code >= NO_RETRY_CODE_MIN && code <= NO_RETRY_CODE_MAX);

export const useWebSocket = ({ onMessage, enabled = true }: UseWebSocketOptions) => {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(RECONNECT_DELAY_MS);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const attemptsWithoutOpenRef = useRef(0);
  const onMessageRef = useRef(onMessage);

  onMessageRef.current = onMessage;

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    const wsUrl = import.meta.env.VITE_WS_URL;

    if (!wsUrl || !mountedRef.current) return;

    const socket = new WebSocket(wsUrl);

    socketRef.current = socket;

    socket.onopen = () => {
      if (socketRef.current !== socket || !mountedRef.current) return;

      reconnectDelayRef.current = RECONNECT_DELAY_MS;
      attemptsWithoutOpenRef.current = 0;

      heartbeatTimerRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    socket.onmessage = (event: MessageEvent) => {
      if (socketRef.current !== socket || !mountedRef.current) return;

      try {
        onMessageRef.current?.(JSON.parse(event.data as string) as WsMessage);
      } catch {
        return;
      }
    };

    socket.onclose = (event: CloseEvent) => {
      if (socketRef.current !== socket) return;

      clearTimers();

      if (!mountedRef.current || isFinalClose(event.code)) return;

      attemptsWithoutOpenRef.current += 1;

      if (attemptsWithoutOpenRef.current > MAX_ATTEMPTS_WITHOUT_OPEN) {
        console.warn('Giving up on the WebSocket: the connection never opened', {
          attempts: attemptsWithoutOpenRef.current,
        });

        return;
      }

      reconnectTimerRef.current = setTimeout(() => {
        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, MAX_RECONNECT_DELAY_MS);
        connect();
      }, reconnectDelayRef.current);
    };

    socket.onerror = () => {
      if (socketRef.current !== socket) return;

      socket.close();
    };
  }, [clearTimers]);

  useEffect(() => {
    mountedRef.current = true;
    attemptsWithoutOpenRef.current = 0;

    if (!enabled) return;

    connect();

    return () => {
      mountedRef.current = false;
      clearTimers();

      const socket = socketRef.current;

      socketRef.current = null;

      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.close();
      }
    };
  }, [enabled, connect, clearTimers]);
};
